/**
 * The boot veil: a deep link to an editor tab (`?tab=studio`, `?tab=picker`) paints its final layout
 * FIRST, instead of painting Quick convert and then rearranging it in front of the visitor.
 *
 * Why it exists (review 7, PAGE lane, measured on QA with a `layout-shift` observer): the static export
 * cannot read the URL, so every converter page is exported with Quick convert selected and the tab is
 * switched in `componentDidMount`. On a deep link that switch hides the H1, the subtitle, the tab strip
 * and the side ad rails, and then the editor chunk arrives and grows the tool container -- CLS 0.15 at
 * 1440, 0.25 at 1024, 0.56 at 768 and 0.35 at 390 on `/convert-mov-to-jpg?tab=picker`, 0.17/0.32 on
 * `/convert-jpg-to-mp4?tab=studio`. Production has no editor tabs and measured 0.00-0.01 on the same
 * URLs, so this is the editors' regression, and it is a Core Web Vitals one: CLS is scored per URL.
 *
 * The mechanism is the simplest one that is correct at every width: a tiny inline script, emitted in the
 * page right before the converter's wrapper, reads `?tab=` BEFORE the wrapper is parsed and -- only when
 * the tab names an editor this page offers -- adds a `<style>` that makes the wrapper and the column
 * holding it `visibility: hidden`. The layout still happens underneath, but the Layout Instability API does not score boxes that
 * are not visible, so the mount-time rearrangement costs nothing. Once the editor has replaced its
 * skeleton and the tool container has held its height for a few frames, the page lifts the veil (a
 * visibility change is not a layout shift). Everything else is untouched: a URL without an editor tab
 * never gets the style, the header and the ad rails outside the wrapper paint as before, and a crawler
 * or a no-JS visitor sees the ordinary page.
 *
 * Why a script-inserted `<style>` and not an attribute plus a CSS-module rule: the build inlines only
 * the CSS whose selectors match the exported HTML (Beasties + PurgeCSS, `beasties-critical-css.js`), so
 * a rule keyed on a runtime attribute would arrive with the async stylesheet -- after first paint, i.e.
 * too late to veil anything. And a style ELEMENT in <head> is outside React's tree, so hydration sees
 * exactly the markup the export wrote.
 *
 * Two guards keep a veil from ever sticking: `BOOT_VEIL_MAX_MS` lifts it from script even if the editor
 * never settles, and the style carries its own CSS failsafe (`BOOT_VEIL_FAILSAFE_MS`) that reveals the
 * wrapper even if the JavaScript bundle never runs at all.
 */

/** The `<style>` element's id -- one per document; the page base removes it. */
export const BOOT_VEIL_ID = 'xc-boot-veil';

/**
 * The inline `<script>` element's own id. The veil also hides the script's PARENT (the ad frame's
 * content column, `div:has(> #xc-boot-veil-src)`), because that column itself moves on a deep link: the
 * side rails leave with the workspace and the one-track grid pulls the column from x=190 to x=0 at 1440
 * (CLS 0.14 on its own, measured). A hidden wrapper inside a visible column did not stop Chrome scoring
 * the column. The wrapper rule stays as the fallback for a browser without `:has()` -- a separate rule,
 * since one unsupported selector in a list drops the whole rule.
 */
export const BOOT_VEIL_SCRIPT_ID = 'xc-boot-veil-src';

/** Script-side ceiling: the veil is lifted this long after mount whatever the editor is doing. */
export const BOOT_VEIL_MAX_MS = 3000;

/**
 * CSS-side ceiling, from the style's insertion: a `visibility` animation that ends visible. Longer than
 * `BOOT_VEIL_MAX_MS` plus a slow hydration on a phone, short enough that a broken bundle still shows the
 * page.
 */
export const BOOT_VEIL_FAILSAFE_MS = 6000;

/** Frames the tool container must keep one height (with no skeleton in it) before the veil lifts. */
export const BOOT_VEIL_STABLE_FRAMES = 3;

/** The loading placeholders the two editor chunks render (`StudioSkeleton`, `PickerSkeleton`). */
export const EDITOR_SKELETON_SELECTOR = '[data-testid="studio-skeleton"],[data-testid="picker-skeleton"]';

/**
 * The inline script's source. `tabs` are the editor tab ids THIS page offers (its page half of the gate,
 * a pure function of props, so the export and the hydrating client emit the same string); `selector` is
 * the wrapper's class selector. Returns '' when the page offers no editor -- the caller then renders no
 * script at all, so the ~5,000 plain converter pages are byte-identical.
 */
export function bootVeilScript(tabs: readonly string[], selector: string): string {
    if (!tabs.length || !selector) return '';
    const hide = `{visibility:hidden;animation:${BOOT_VEIL_ID} 0s linear ${BOOT_VEIL_FAILSAFE_MS}ms forwards}`;
    const css = `${selector}${hide}div:has(>#${BOOT_VEIL_SCRIPT_ID})${hide}`
        + `@keyframes ${BOOT_VEIL_ID}{to{visibility:visible}}`;
    return '(function(){try{'
        + `var t=new URLSearchParams(location.search).get('tab');`
        + `if(${JSON.stringify(tabs)}.indexOf(t)<0||document.getElementById(${JSON.stringify(BOOT_VEIL_ID)}))return;`
        + `var s=document.createElement('style');s.id=${JSON.stringify(BOOT_VEIL_ID)};`
        + `s.textContent=${JSON.stringify(css)};document.head.appendChild(s)`
        + '}catch(e){}})()';
}

/** Is a veil up right now? */
export function bootVeilActive(doc: Document | undefined = typeof document === 'undefined' ? undefined : document): boolean {
    return !!doc && !!doc.getElementById(BOOT_VEIL_ID);
}

/** Lift the veil (idempotent). */
export function liftBootVeil(doc: Document | undefined = typeof document === 'undefined' ? undefined : document): void {
    const el = doc?.getElementById(BOOT_VEIL_ID);
    if (el && el.parentNode) el.parentNode.removeChild(el);
}

export interface BootVeilWatch {
    /** The box whose height must settle: the tool container. */
    box: () => HTMLElement | null;
    /** The page still wants to wait (an editor is selected and its skeleton is showing). */
    waiting: () => boolean;
    raf?: (cb: () => void) => unknown;
    now?: () => number;
    doc?: Document;
}

/**
 * Lift the veil once the tool container has held one height for `BOOT_VEIL_STABLE_FRAMES` frames with
 * nothing to wait for, or after `BOOT_VEIL_MAX_MS`. Does nothing when no veil is up. Returns a cancel
 * that also lifts the veil (the page calls it on unmount, so a client-side hop away never inherits it).
 */
export function watchBootVeil(w: BootVeilWatch): () => void {
    const doc = w.doc ?? (typeof document === 'undefined' ? undefined : document);
    if (!bootVeilActive(doc)) return () => undefined;
    const raf = w.raf ?? ((cb: () => void) => requestAnimationFrame(cb));
    const now = w.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    const started = now();
    let done = false;
    let lastHeight = -1;
    let stable = 0;
    const finish = () => { done = true; liftBootVeil(doc); };
    const tick = () => {
        if (done) return;
        const box = w.box();
        const height = box ? Math.round(box.getBoundingClientRect().height) : -1;
        stable = !w.waiting() && height === lastHeight ? stable + 1 : 0;
        lastHeight = height;
        if (stable >= BOOT_VEIL_STABLE_FRAMES || now() - started >= BOOT_VEIL_MAX_MS) { finish(); return; }
        raf(tick);
    };
    raf(tick);
    return finish;
}
